import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export interface Material {
  id: number;
  title: string;
  content: string;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  author: string;
  phone: string;
  department: string;
  view_count: number;
  download_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

interface MaterialContextType {
  materials: Material[];
  loading: boolean;
  addMaterial: (material: Omit<Material, 'id' | 'view_count' | 'download_count' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateMaterial: (id: number, material: Partial<Material>) => Promise<void>;
  deleteMaterial: (id: number) => Promise<void>;
  getMaterial: (id: number) => Material | undefined;
  incrementViewCount: (id: number) => Promise<void>;
  incrementDownloadCount: (id: number) => Promise<void>;
  refreshMaterials: () => Promise<void>;
}

const MaterialContext = createContext<MaterialContextType | undefined>(undefined);

export const MaterialProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('materials')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMaterials(data || []);
    } catch (error) {
      console.error('Error fetching materials:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  const addMaterial = async (material: Omit<Material, 'id' | 'view_count' | 'download_count' | 'created_at' | 'updated_at'>) => {
    try {
      const { error } = await supabase
        .from('materials')
        .insert({
          ...material,
          view_count: 0,
          download_count: 0
        });

      if (error) throw error;
      await fetchMaterials();
    } catch (error) {
      console.error('Error adding material:', error);
      throw error;
    }
  };

  const updateMaterial = async (id: number, material: Partial<Material>) => {
    try {
      const { error } = await supabase
        .from('materials')
        .update({ ...material, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      await fetchMaterials();
    } catch (error) {
      console.error('Error updating material:', error);
      throw error;
    }
  };

  const deleteMaterial = async (id: number) => {
    try {
      const { error } = await supabase
        .from('materials')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setMaterials(prev => prev.filter(m => m.id !== id));
    } catch (error) {
      console.error('Error deleting material:', error);
      throw error;
    }
  };

  const getMaterial = (id: number) => {
    return materials.find(m => m.id === id);
  };

  const incrementViewCount = async (id: number) => {
    try {
      const material = materials.find(m => m.id === id);
      if (!material) return;

      await supabase
        .from('materials')
        .update({ view_count: material.view_count + 1 })
        .eq('id', id);

      setMaterials(prev =>
        prev.map(m => m.id === id ? { ...m, view_count: m.view_count + 1 } : m)
      );
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const incrementDownloadCount = async (id: number) => {
    try {
      const material = materials.find(m => m.id === id);
      if (!material) return;

      await supabase
        .from('materials')
        .update({ download_count: material.download_count + 1 })
        .eq('id', id);

      setMaterials(prev =>
        prev.map(m => m.id === id ? { ...m, download_count: m.download_count + 1 } : m)
      );
    } catch (error) {
      console.error('Error incrementing download count:', error);
    }
  };

  const refreshMaterials = async () => {
    await fetchMaterials();
  };

  return (
    <MaterialContext.Provider value={{
      materials,
      loading,
      addMaterial,
      updateMaterial,
      deleteMaterial,
      getMaterial,
      incrementViewCount,
      incrementDownloadCount,
      refreshMaterials
    }}>
      {children}
    </MaterialContext.Provider>
  );
};

export const useMaterials = () => {
  const context = useContext(MaterialContext);
  if (context === undefined) {
    throw new Error('useMaterials must be used within a MaterialProvider');
  }
  return context;
};
